import React, { useEffect, useMemo, useState } from 'react'
import { Box, Dialog, IconButton, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import { Button, CopyLinkField, KeyRow, KeysList } from '@/design-system'
import { fireAlertToast } from '@/design-system/alerts'

export interface ShareSheetProps {
  open: boolean
  onClose: () => void
  shareUrl: string
  title?: string
  /** Share code shown in key meta, e.g. "EM-0614". Defaults to last segment of shareUrl. */
  shareCode?: string
  /** How many times the link has been opened */
  openCount?: number
  /**
   * Switches the sheet into privacy-aware mode (galleries): a Private/Public
   * segmented toggle replaces the album's "every key is revocable" subtitle,
   * the link + QR section locks behind Public, and going Private -> Public
   * runs through one extra confirm step first (Public -> Private is instant).
   * Omit entirely for the album's existing always-shareable invite-key model
   * — leaves that path pixel-identical to before.
   */
  isPrivate?: boolean
  /** Flips visibility. Called immediately for Public -> Private; called only after the confirm step for Private -> Public. */
  onTogglePrivacy?: () => void | Promise<void>
  /**
   * Album mode: when set, show Downloads → Originals On/Off (topicXData.allowDownload).
   * Omit for galleries — they don't use this control.
   */
  allowDownload?: boolean
  /** Persist allowDownload (album Share only). */
  onAllowDownloadChange?: (next: boolean) => void | Promise<void>
  /**
   * Album mode: when set, show Downloads → Upload quality Standard/Full
   * (topicXData.fullResolutionUploads). Same field the create-flow quality
   * step also writes — this row lets the owner change it after the fact.
   * Omit for galleries — they don't use this control.
   */
  fullResolutionUploads?: boolean
  /** Persist fullResolutionUploads (album Share only). */
  onFullResolutionUploadsChange?: (next: boolean) => void | Promise<void>
  /** Free tier — row renders locked with an upgrade CTA instead of the segmented toggle. */
  uploadQualityLocked?: boolean
  /** Opens the upgrade modal for FULL_RESOLUTION_UPLOAD — required when uploadQualityLocked is true. */
  onUpgradeUploadQuality?: () => void
  /**
   * Stack above immersive surfaces. Album Lightbox sits at 1400 — pass
   * something higher (e.g. 1600) when opening share from inside it.
   */
  zIndex?: number
}

/* ── night shell — same language as UpgradeModal / SaveScreenshotSheet ── */

const Paper = styled(Box)({
  position: 'relative',
  backgroundColor: 'rgba(23,22,29,0.98)',
  color: 'var(--ds-night-ink, #f4f3f6)',
  borderRadius: 20,
  overflow: 'hidden',
  maxWidth: 460,
  width: '100%',
  border: '1px solid rgba(244,243,246,0.12)',
  boxShadow: '0 28px 80px rgba(0,0,0,0.55)',
  /* Remap light DS tokens so nested CopyLinkField / KeyRow / Button inherit night. */
  ['--ds-canvas' as string]: 'rgba(244,243,246,0.06)',
  ['--ds-surface' as string]: 'rgba(244,243,246,0.06)',
  ['--ds-ink' as string]: 'var(--ds-night-ink, #f4f3f6)',
  ['--ds-ink-muted' as string]: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6))',
  ['--ds-ink-subtle' as string]: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  ['--ds-border' as string]: 'var(--ds-night-border, rgba(244,243,246,0.1))',
  ['--ds-border-strong' as string]: 'rgba(244,243,246,0.18)',
  ['--ds-hover' as string]: 'rgba(244,243,246,0.08)',
  ['--ds-inverse' as string]: 'var(--ds-night-ink, #f4f3f6)',
  ['--ds-inverse-ink' as string]: '#17130a',
})

const SpectrumEdge = styled(Box)({
  height: 2.5,
  background: 'var(--ds-gradient-spectrum, linear-gradient(100deg, #ffcf7d, #fea67d, #be9dfd, #01dafe))',
  flexShrink: 0,
})

const Head = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '20px 24px 16px',
  borderBottom: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
})

const HeadTitle = styled(Typography)({
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  lineHeight: 1.2,
  color: 'var(--ds-night-ink, #f4f3f6)',
})

const HeadSub = styled(Typography)({
  fontFamily: 'var(--ds-font-mono, "IBM Plex Mono", monospace)',
  fontSize: 9,
  letterSpacing: '0.12em',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  marginTop: 2,
})

const CloseBtn = styled(IconButton)({
  flexShrink: 0,
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  '&:hover': {
    color: 'var(--ds-night-ink, #f4f3f6)',
    background: 'rgba(244,243,246,0.1)',
  },
})

const Body = styled(Box)({
  padding: '20px 24px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
})

const Label = styled(Typography)({
  fontFamily: 'var(--ds-font-mono, "IBM Plex Mono", monospace)',
  fontSize: 9,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  marginBottom: 10,
})

/* ── privacy toggle — galleries only (isPrivate !== undefined) ────────── */

const PrivacyRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
})

const PrivacyTitle = styled(Typography)({
  fontSize: 13,
  fontWeight: 650,
  color: 'var(--ds-night-ink, #f4f3f6)',
})
const PrivacySub = styled(Typography)({
  fontSize: 11.5,
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  marginTop: 1,
})

const Seg2 = styled(Box)({
  display: 'flex',
  padding: 3,
  borderRadius: 11,
  background: 'rgba(244,243,246,0.08)',
  flexShrink: 0,
})

const SegBtn = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  padding: '7px 14px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 650,
  color: $on ? '#17130a' : 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  background: $on ? 'var(--ds-night-ink, #f4f3f6)' : 'transparent',
  boxShadow: $on ? '0 1px 4px rgba(0,0,0,0.35)' : 'none',
  transition: 'background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)',
  '&:disabled': { opacity: 0.45, cursor: 'default' },
}))

const RowDivider = styled(Box)({
  height: 1,
  background: 'var(--ds-night-border, rgba(244,243,246,0.1))',
  margin: '14px 0',
})

const LockedHint = styled('button')({
  all: 'unset',
  cursor: 'pointer',
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.02em',
  color: 'var(--ds-accent-dot-3, #be9dfd)',
  flexShrink: 0,
  whiteSpace: 'nowrap',
})

const MakePublicCta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 12,
  border: '1px dashed rgba(244,243,246,0.18)',
})

/* ── QR key row ─────────────────────────────────────────────────────── */

const QrRow = styled(Box)({
  display: 'flex',
  gap: 14,
  alignItems: 'center',
  padding: 14,
  borderRadius: 14,
  background: 'var(--ds-gradient-spectrum-soft, rgba(190,157,253,0.1))',
  boxShadow: 'inset 0 0 0 1px rgba(244,243,246,0.06)',
})

const QrImgWrap = styled(Box, { shouldForwardProp: (p) => p !== '$locked' })<{ $locked?: boolean }>(({ $locked }) => ({
  width: 74,
  height: 74,
  flexShrink: 0,
  borderRadius: 10,
  overflow: 'hidden',
  background: '#fff',
  boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  filter: $locked ? 'grayscale(1) blur(1.5px)' : 'none',
  opacity: $locked ? 0.5 : 1,
  '& img': { width: '100%', height: '100%', display: 'block' },
}))

const qrSrc = (url: string, size = 180) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(url)}`

/* ── confirm sub-state — replaces the body, no moderator/review, just a
   deliberate self-serve friction point before flipping visibility ────── */

const ConfirmBody = styled(Box)({ textAlign: 'center', padding: '8px 4px 4px' })
const ConfirmIcon = styled(Box)({
  width: 46,
  height: 46,
  borderRadius: '50%',
  margin: '0 auto 14px',
  background: 'rgba(190,157,253,0.16)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  color: 'var(--ds-accent-dot-3, #be9dfd)',
})
const ConfirmTitle = styled(Typography)({
  fontSize: 15,
  fontWeight: 700,
  marginBottom: 6,
  color: 'var(--ds-night-ink, #f4f3f6)',
})
const ConfirmCopy = styled(Typography)({
  fontSize: 12.5,
  color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6))',
  lineHeight: 1.55,
  maxWidth: 320,
  margin: '0 auto 20px',
})
const ConfirmActions = styled(Box)({ display: 'flex', gap: 8, justifyContent: 'center' })

/* ── component ──────────────────────────────────────────────────────── */

export function ShareSheet({
  open,
  onClose,
  shareUrl,
  title = 'Album',
  shareCode,
  openCount,
  isPrivate,
  onTogglePrivacy,
  allowDownload,
  onAllowDownloadChange,
  fullResolutionUploads,
  onFullResolutionUploadsChange,
  uploadQualityLocked,
  onUpgradeUploadQuality,
  zIndex,
}: ShareSheetProps) {
  const privacyAware = isPrivate !== undefined
  const isPublic = !isPrivate
  const downloadsAware = allowDownload !== undefined && !!onAllowDownloadChange
  const uploadQualityAware = fullResolutionUploads !== undefined && !!onFullResolutionUploadsChange

  // Reopening (or closing) always lands back on the normal body — a stale
  // confirm step surviving a close/reopen would show "Make public?" for a
  // gallery the owner already made public last time, or vice versa.
  const [confirming, setConfirming] = useState(false)
  const [pending, setPending] = useState(false)
  const [downloadPending, setDownloadPending] = useState(false)
  const [qualityPending, setQualityPending] = useState(false)
  // Optimistic mirror so Seg2 feels instant; resync when prop changes.
  const [downloadOn, setDownloadOn] = useState(allowDownload !== false)
  const [qualityFullOn, setQualityFullOn] = useState(fullResolutionUploads === true)
  useEffect(() => {
    if (!open) {
      setConfirming(false)
      setPending(false)
      setDownloadPending(false)
      setQualityPending(false)
    }
  }, [open])
  useEffect(() => {
    if (allowDownload !== undefined) setDownloadOn(allowDownload)
  }, [allowDownload])
  useEffect(() => {
    if (fullResolutionUploads !== undefined) setQualityFullOn(fullResolutionUploads)
  }, [fullResolutionUploads])

  const code = useMemo(() => {
    if (shareCode) return shareCode
    const parts = shareUrl.split('/')
    return parts[parts.length - 1]?.toUpperCase() ?? ''
  }, [shareUrl, shareCode])

  const keyMeta = [code, openCount != null ? `${openCount} OPENS` : null, 'GUESTS CAN VIEW'].filter(Boolean).join(' · ')

  const onDownloadQr = async () => {
    try {
      const res = await fetch(qrSrc(shareUrl, 300))
      const blob = await res.blob()
      const a = document.createElement('a')
      a.download = `${code}-qr.png`
      a.href = URL.createObjectURL(blob)
      a.click()
      URL.revokeObjectURL(a.href)
    } catch {
      fireAlertToast('share.qrDownloadFailed')
    }
  }

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  const requestPublic = () => setConfirming(true)
  const cancelConfirm = () => setConfirming(false)
  const confirmPublic = async () => {
    setPending(true)
    try {
      await onTogglePrivacy?.()
    } finally {
      setPending(false)
      setConfirming(false)
    }
  }

  const setAllowDownload = async (next: boolean) => {
    if (!onAllowDownloadChange || downloadPending || next === downloadOn) return
    const prev = downloadOn
    setDownloadOn(next)
    setDownloadPending(true)
    try {
      await onAllowDownloadChange(next)
    } catch {
      setDownloadOn(prev)
    } finally {
      setDownloadPending(false)
    }
  }

  const setFullResolutionUploads = async (next: boolean) => {
    if (!onFullResolutionUploadsChange || qualityPending || next === qualityFullOn) return
    const prev = qualityFullOn
    setQualityFullOn(next)
    setQualityPending(true)
    try {
      await onFullResolutionUploadsChange(next)
    } catch {
      setQualityFullOn(prev)
    } finally {
      setQualityPending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        ...(zIndex != null ? { zIndex } : null),
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(5,5,8,0.72)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          background: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          margin: 2,
          maxWidth: 460,
          width: '100%',
          overflow: 'visible',
        },
      }}
    >
      <Paper>
        <SpectrumEdge />
        {confirming ? (
          <>
            <Head>
              <Box minWidth={0}>
                <HeadTitle noWrap>Make &quot;{title}&quot; public?</HeadTitle>
              </Box>
              <CloseBtn aria-label='Cancel' onClick={cancelConfirm} size='small' disabled={pending}>
                <CloseIcon fontSize='small' />
              </CloseBtn>
            </Head>
            <Body>
              <ConfirmBody>
                <ConfirmIcon aria-hidden>◇</ConfirmIcon>
                <ConfirmTitle>Anyone with the link can view this gallery</ConfirmTitle>
                <ConfirmCopy>
                  No account needed to open it — they&apos;ll land in a read-only walkthrough. You can make it private
                  again anytime; the link stops working immediately.
                </ConfirmCopy>
                <ConfirmActions>
                  <Button variant='ghost' size='sm' onClick={cancelConfirm} disabled={pending}>
                    Cancel
                  </Button>
                  <Button variant='primary' size='sm' onClick={confirmPublic} loading={pending}>
                    Make public
                  </Button>
                </ConfirmActions>
              </ConfirmBody>
            </Body>
          </>
        ) : (
          <>
            {/* ── header ── */}
            <Head>
              <Box minWidth={0}>
                <HeadTitle noWrap>Share &quot;{title}&quot;</HeadTitle>
                <HeadSub>
                  {privacyAware
                    ? isPublic
                      ? 'PUBLIC · GUESTS CAN VIEW WITHOUT AN ACCOUNT'
                      : 'PRIVATE · ONLY YOU CAN VIEW'
                    : 'EVERY KEY IS REVOCABLE · OPENS COUNTED, NEVER NAMED'}
                </HeadSub>
              </Box>
              <CloseBtn aria-label='Close' onClick={onClose} size='small'>
                <CloseIcon fontSize='small' />
              </CloseBtn>
            </Head>

            {/* ── body ── */}
            <Body>
              {privacyAware && (
                <Box>
                  <Label>Visibility</Label>
                  <PrivacyRow>
                    <Box minWidth={0}>
                      <PrivacyTitle>{isPublic ? 'Anyone with the link' : 'Only you'}</PrivacyTitle>
                      <PrivacySub>
                        {isPublic ? 'No sign-in required to walk the room' : 'Make it public to get a shareable link'}
                      </PrivacySub>
                    </Box>
                    <Seg2>
                      <SegBtn type='button' $on={!isPublic} onClick={() => isPublic && onTogglePrivacy?.()}>
                        Private
                      </SegBtn>
                      <SegBtn type='button' $on={isPublic} onClick={() => !isPublic && requestPublic()}>
                        Public
                      </SegBtn>
                    </Seg2>
                  </PrivacyRow>
                </Box>
              )}

              {!privacyAware || isPublic ? (
                <>
                  {/* The link */}
                  <Box>
                    <Label>The link</Label>
                    <CopyLinkField value={shareUrl} onCopy={() => fireAlertToast('share.linkCopied')} />
                  </Box>

                  {/* The QR key */}
                  <Box>
                    <Label>The QR key</Label>
                    <QrRow>
                      <QrImgWrap>
                        <img src={qrSrc(shareUrl)} alt={`QR code for ${title}`} />
                      </QrImgWrap>
                      <Box flex={1} minWidth={0}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 650,
                            letterSpacing: '-0.01em',
                            color: 'var(--ds-night-ink, #f4f3f6)',
                          }}
                        >
                          Print-ready key card
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
                            mt: 0.25,
                            lineHeight: 1.45,
                          }}
                        >
                          Table tent, invite insert, or sticker — guests scan and walk in.
                        </Typography>
                        <Box display='flex' gap={1} mt={1}>
                          <Button variant='night' size='sm' onClick={onDownloadQr}>
                            ⤓ PNG
                          </Button>
                          {canNativeShare && (
                            <Button
                              variant='night'
                              size='sm'
                              onClick={() => navigator.share({ title, url: shareUrl }).catch(() => {})}
                            >
                              Share…
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </QrRow>
                  </Box>
                </>
              ) : (
                <>
                  <Box>
                    <Label>The QR key</Label>
                    <QrRow>
                      <QrImgWrap $locked>
                        <img src={qrSrc(shareUrl)} alt='' />
                      </QrImgWrap>
                      <Box flex={1} minWidth={0}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 650,
                            letterSpacing: '-0.01em',
                            color: 'var(--ds-night-ink, #f4f3f6)',
                          }}
                        >
                          No link yet
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
                            mt: 0.25,
                            lineHeight: 1.45,
                          }}
                        >
                          This gallery is private — make it public to generate a scannable key and shareable link.
                        </Typography>
                      </Box>
                    </QrRow>
                  </Box>

                  <MakePublicCta>
                    <Typography sx={{ fontSize: 12, color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))' }}>
                      Ready to share this with guests?
                    </Typography>
                    <Button variant='primary' size='sm' onClick={requestPublic}>
                      Make public
                    </Button>
                  </MakePublicCta>
                </>
              )}

              {/* Active keys — album's revocable invite-key model only; galleries
                  are a single public/private flag, not a list of issued keys. */}
              {!privacyAware && (
                <Box>
                  <Label>Active keys · 1</Label>
                  <KeysList>
                    <KeyRow title='Album key' meta={keyMeta} />
                  </KeysList>
                </Box>
              )}

              {(downloadsAware || uploadQualityAware) && (
                <Box>
                  <Label>Downloads</Label>
                  {downloadsAware && (
                    <PrivacyRow>
                      <Box minWidth={0}>
                        <PrivacyTitle>Originals</PrivacyTitle>
                        <PrivacySub>
                          {downloadOn
                            ? 'Guests can save full-resolution photos from the lightbox.'
                            : 'Lightbox download is hidden — viewing only.'}
                        </PrivacySub>
                      </Box>
                      <Seg2>
                        <SegBtn
                          type='button'
                          $on={downloadOn}
                          disabled={downloadPending}
                          onClick={() => setAllowDownload(true)}
                        >
                          On
                        </SegBtn>
                        <SegBtn
                          type='button'
                          $on={!downloadOn}
                          disabled={downloadPending}
                          onClick={() => setAllowDownload(false)}
                        >
                          Off
                        </SegBtn>
                      </Seg2>
                    </PrivacyRow>
                  )}
                  {downloadsAware && uploadQualityAware && <RowDivider />}
                  {uploadQualityAware && (
                    <PrivacyRow>
                      <Box minWidth={0}>
                        <PrivacyTitle>Upload quality</PrivacyTitle>
                        <PrivacySub>
                          {uploadQualityLocked
                            ? 'Full uploads at up to 4000px. Gallery+.'
                            : qualityFullOn
                              ? 'New uploads land at up to 4000px.'
                              : 'New uploads land at 1000px — fast to share.'}
                        </PrivacySub>
                      </Box>
                      {uploadQualityLocked ? (
                        <LockedHint type='button' onClick={onUpgradeUploadQuality}>
                          Upgrade →
                        </LockedHint>
                      ) : (
                        <Seg2>
                          <SegBtn
                            type='button'
                            $on={!qualityFullOn}
                            disabled={qualityPending}
                            onClick={() => setFullResolutionUploads(false)}
                          >
                            Standard
                          </SegBtn>
                          <SegBtn
                            type='button'
                            $on={qualityFullOn}
                            disabled={qualityPending}
                            onClick={() => setFullResolutionUploads(true)}
                          >
                            Full
                          </SegBtn>
                        </Seg2>
                      )}
                    </PrivacyRow>
                  )}
                </Box>
              )}
            </Body>
          </>
        )}
      </Paper>
    </Dialog>
  )
}
